/**
 * Paints a sword from its parts (parts.ts) pixel by pixel at the figure's full resolution: blade, collar, grip,
 * guard, pommel, tassel; and its scabbard along a carry line. The figure's finish (rig/finish.ts) then lights it
 * and lays the dark outline round it like every other piece.
 */
import { BLADES, BODIES, GRIPS, GUARDS, METALS, POMMELS, type Ramp, SCABBARDS, SILKS, STEELS, type SwordSpec, TASSELS } from './parts';
import { GEMS } from './materials';
import './shapes';
import './materials';

type G = CanvasRenderingContext2D;
type Pt = { x: number; y: number };

const FALLBACK: Ramp = ['#efe7d6', '#c8d2dc', '#9aa8b8', '#626c84'];
const ramp = (m: Map<string, Ramp>, id: string | undefined, fb: Ramp = FALLBACK): Ramp => (id && m.get(id)) || fb;

/**
 * Rasterises along a line through `o` (fine px, through pixel centres) pointing `angle`: `paint(a, c, lit)` gets
 * `a` art px along (scaled by `fore`), `c` fine px across, `lit` which side of `c` faces the light (upper right).
 */
function raster(
  g: G,
  o: Pt,
  angle: number,
  res: number,
  fore: number,
  a0: number,
  a1: number,
  halfMax: number,
  paint: (a: number, c: number, lit: number) => string | undefined,
): void {
  const d = { x: Math.cos(angle), y: Math.sin(angle) };
  const n = { x: -d.y, y: d.x };
  const lit = n.x * 0.6 - n.y * 0.8 >= 0 ? 1 : -1;
  const s = res * Math.max(0.2, fore);
  const ends = [a0, a1].map((k) => ({ x: o.x + d.x * k * s, y: o.y + d.y * k * s }));
  const pad = halfMax + 2;
  const x0 = Math.floor(Math.min(ends[0]!.x, ends[1]!.x) - pad);
  const x1 = Math.ceil(Math.max(ends[0]!.x, ends[1]!.x) + pad);
  const y0 = Math.floor(Math.min(ends[0]!.y, ends[1]!.y) - pad);
  const y1 = Math.ceil(Math.max(ends[0]!.y, ends[1]!.y) + pad);
  for (let Y = y0; Y <= y1; Y++)
    for (let X = x0; X <= x1; X++) {
      const vx = X - o.x;
      const vy = Y - o.y;
      const a = (vx * d.x + vy * d.y) / s;
      if (a < a0 || a > a1) continue;
      const c = vx * n.x + vy * n.y;
      if (Math.abs(c) > halfMax + 0.5) continue;
      const col = paint(a, c, lit);
      if (!col) continue;
      g.fillStyle = col;
      g.fillRect(X, Y, 1, 1);
    }
}

/** A tone −1 … 1 on a 4-tone ramp. */
const tone = (r: Ramp, v: number) => (v > 0.55 ? r[0] : v > -0.05 ? r[1] : v > -0.6 ? r[2] : r[3]);

/** The sword's length ahead of the hand (art px). */
export const swordLength = (s: SwordSpec): number => BLADES.get(s.blade.profile)?.length ?? 26;
/** How far the grip and pommel run behind the hand (art px). */
export const SWORD_BEHIND = 5;

/**
 * Paints `s` with its grip at `hand` (fine px) pointing `angle`. `shown` cuts the blade to that many art px (a
 * sword in its scabbard shows its hilt only); `sway(i, n)` moves the tassel's strands with the figure.
 */
export function paintSword(
  g: G,
  s: SwordSpec,
  hand: Pt,
  angle: number,
  res: number,
  fore = 1,
  shown?: number,
  sway: (i: number, n: number) => number = () => 0,
): void {
  const profile = BLADES.get(s.blade.profile) ?? BLADES.get('hero')!;
  const guard = GUARDS.get(s.guard.shape) ?? GUARDS.get('wings')!;
  const pom = POMMELS.get(s.pommel.shape) ?? POMMELS.get('cap')!;
  const steel = ramp(STEELS, s.blade.steel);
  const fit = ramp(METALS, s.guard.metal);
  const pfit = ramp(METALS, s.pommel.metal ?? s.guard.metal);
  const silk = ramp(SILKS, s.grip.silk);
  const B = s.blade;
  const full = profile.length;
  const L = Math.min(shown ?? full, full);
  const a0 = guard.end;
  const root = profile.half(0);
  const deco = B.deco ?? 'ridge';

  // ── blade ──
  if (L > a0 + 0.2)
    raster(g, hand, angle, res, fore, a0, L, root + 1.2, (a, c, lit) => {
      const t = (a - a0) / (full - a0);
      const h = profile.half(t);
      const ac = Math.abs(c);
      if (ac > h) return undefined;
      const u = Math.sign(c) * lit;
      // The point: a one-pixel line glinting at its very end.
      if (h < 1.05) return t > 0.96 ? steel[0] : steel[1];
      const edge = ac > h - 0.75;
      if (B.edge && edge && t < 0.97) return B.edge;
      if (B.inlay && ac < 0.5 && t < 0.6) return B.inlay;
      if (B.stars && ac < 0.5 && t < 0.78 && Math.round(a * 1.25) % 3 === 0) return B.stars;
      if ((deco === 'ridge' || deco === 'fuller') && ac < 0.5) return steel[0];
      if (deco === 'fuller' && h >= 1.9 && u < 0 && ac > 0.5 && ac < 1.4 && t < 0.72) return steel[3];
      if (deco === 'twin' && h >= 1.9 && ac > 0.5 && ac < 1.4 && t < 0.62) return u > 0 ? steel[2] : steel[3];
      // The finish outlines the blade dark, so its faces stay light: the lit face light, the shaded face mid, split by
      // the glinting ridge — the diamond section in three clean bands (a glint on the edge too dotted the diagonals).
      let col = u > 0 ? steel[1] : steel[2];
      if (B.pattern && !edge && Math.floor(a * 1.6 + c * 0.9) % 3 === 0) col = u > 0 ? B.pattern : steel[3];
      return col;
    });
  // ── collar under the guard ──
  raster(g, hand, angle, res, fore, a0 - 0.1, a0 + 0.5, root + 1, (_a, c, lit) =>
    Math.abs(c) <= root + 0.45 ? tone(fit, (c / (root + 0.45)) * lit) : undefined,
  );
  // ── grip ──
  const wrap = GRIPS.get(s.grip.wrap) ?? GRIPS.get('cord')!;
  raster(g, hand, angle, res, fore, -SWORD_BEHIND, 0.3, 1.6, (a, c, lit) => {
    if (Math.abs(c) > 1.25) return undefined;
    const u = Math.sign(c) * lit;
    if (a > -0.15 || a < -SWORD_BEHIND + 0.45) return tone(fit, u * 0.9);
    return wrap.at(a, c, u, silk[2], silk[1], res);
  });
  // ── guard ──
  const gem = s.guard.gem ? GEMS[s.guard.gem] : undefined;
  raster(g, hand, angle, res, fore, 0, guard.end + 0.4, guard.maxHalf, (a, c, lit) => {
    const v = guard.at(a, Math.abs(c), Math.sign(c) * lit);
    if (v === undefined) return undefined;
    if (v === 'gem') return gem ? (c * lit > 0.2 ? gem[1] : gem[0]) : tone(fit, 0.5);
    if (v === 'eye') return gem ? gem[0] : fit[3];
    return tone(fit, v);
  });
  // ── pommel ──
  const pk = -SWORD_BEHIND;
  const lenPx = pom.len * res * Math.max(0.2, fore);
  const pgem = s.pommel.gem ? GEMS[s.pommel.gem] : gem;
  raster(g, hand, angle, res, fore, pk - pom.len, pk + 0.1, pom.maxHalf + 1, (a, c, lit) => {
    const v = pom.at((pk - a) / pom.len, Math.abs(c), Math.sign(c) * lit, lenPx);
    if (v === undefined) return undefined;
    if (v === 'gem') return pgem ? pgem[c * lit > 0 ? 1 : 0] : tone(pfit, 0.9);
    return tone(pfit, v);
  });
  // ── tassel: through the pommel, hanging under gravity ──
  if (s.tassel) {
    const style = TASSELS.get(s.tassel.style);
    if (style) {
      const d = { x: Math.cos(angle), y: Math.sin(angle) };
      const k = (pk - pom.len * pom.hang) * res * Math.max(0.2, fore);
      const top = { x: Math.round(hand.x + d.x * k), y: Math.round(hand.y + d.y * k) + 1 };
      const put = (x: number, y: number, col: string) => {
        g.fillStyle = col;
        g.fillRect(Math.round(x), Math.round(y), 1, 1);
      };
      const bead = s.tassel.bead ? GEMS[s.tassel.bead]?.[0] : undefined;
      style.draw(put, top, ramp(SILKS, s.tassel.silk), bead, (i, n) => Math.round(sway(i, n)));
    }
  }
}

/** The scabbard along a carry line from its mouth at `at` (art px) pointing `angle`. */
export function paintScabbard(g: G, s: SwordSpec, at: Pt, angle: number, res: number): void {
  const st = SCABBARDS.get(s.scabbard.style) ?? SCABBARDS.get('cloud')!;
  const body = ramp(BODIES, s.scabbard.body, ['#4e4250', '#3b3038', '#2a2026', '#1b1315']);
  const fit = ramp(METALS, s.scabbard.metal ?? s.guard.metal);
  const profile = BLADES.get(s.blade.profile) ?? BLADES.get('hero')!;
  const len = profile.length + 1.2;
  const start = 0.6;
  const mouth = profile.half(0) + st.wider;
  const o = { x: at.x * res, y: at.y * res };
  const chapeStart = len - st.chapeLen;
  raster(g, o, angle, res, 1, start, len, mouth + 1.5, (a, c, lit) => {
    const t = (a - start) / (len - start);
    let h = mouth * (1 - 0.16 * t);
    if (a > chapeStart) {
      const q = (a - chapeStart) / st.chapeLen;
      if (st.chape === 'trap') h = mouth * 0.82 + q * 0.9;
      else if (st.chape === 'round') h *= q > 0.6 ? Math.sqrt(Math.max(0, 1 - ((q - 0.6) / 0.4) ** 2)) : 1;
      else if (st.chape === 'cloud') h *= q < 0.35 ? 1 + q * 0.6 : (1.21 - (q - 0.35) * 1.4);
      else if (st.chape === 'flare') h *= q < 0.5 ? 1 + q * 0.8 : 1.4 * Math.sqrt(Math.max(0, 1 - ((q - 0.5) / 0.5) ** 2));
      else h *= 1 - q * 0.8;
    }
    const ac = Math.abs(c);
    if (ac > h) return undefined;
    const u = (c / Math.max(0.6, h)) * lit;
    const inBand = st.bands.some((b) => {
      const k = start + b * (len - start);
      return a >= k && a <= k + 0.6;
    });
    if (a < start + st.locket || a > chapeStart || inBand) return tone(fit, u);
    if (st.wrap && a < start + st.locket + st.wrap) return (Math.floor(a * res * 0.75) + (c > 0 ? 1 : 0)) % 2 ? body[2] : body[1];
    if (st.slide) {
      const k0 = start + st.slide[0] * (len - start);
      const k1 = start + st.slide[1] * (len - start);
      if (a >= k0 && a <= k1 && ac <= h - 0.3) return tone(fit, a < k0 + 0.5 || a > k1 - 0.5 ? -0.7 : u * 0.8 + 0.2);
    }
    // Lacquer: the lit rim, a gloss line just inside it, the body, the shade, the deep edge.
    const rim = 1 - 0.7 / Math.max(1, h);
    return u > rim ? body[1] : u > rim - 0.42 ? body[0] : u > 0.1 ? body[1] : u < -rim ? body[3] : body[2];
  });
}
